import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, ErrorState, Field, LoadingState, Select, TextArea, TextInput } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { AdminApplicationDetail, AdminReference, CatalogLifecycleStatus } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

const LIFECYCLE_STATUSES: CatalogLifecycleStatus[] = [
  "draft", "internal", "preview", "beta", "available", "maintenance", "suspended", "deprecated", "retired", "coming_soon",
];

function useSaver() {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await fn();
      setNotice(`${label} saved.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `Failed to save ${label}.`);
    } finally {
      setBusy(false);
    }
  }
  return { notice, error, busy, run };
}

export function AdminApplication() {
  const { slug = "" } = useParams();
  if (slug === "new") return <CreateApplication />;
  return <EditApplication slug={slug} />;
}

function CreateApplication() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [primaryEntitlement, setPrimaryEntitlement] = useState("");
  const [icon, setIcon] = useState("📦");
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setError(null);
    try {
      await api.createAdminApplication({ slug: slug.trim(), name: name.trim(), primaryEntitlement: primaryEntitlement.trim(), icon });
      navigate(`/studio/catalog-admin/apps/${slug.trim()}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to create application.");
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>New application</h1>
          <p>Create a catalog application. It starts in <code>draft</code> until you publish it.</p>
        </div>
        <Link to="/studio/catalog-admin"><Button size="sm" variant="ghost">Back</Button></Link>
      </div>
      <Card>
        <CardBody>
          <div className="ods-form-grid">
            <Field label="Slug" help="Unique key that is safe in a URL, e.g. app-vendor">{(id) => <TextInput id={id} value={slug} onChange={(e) => setSlug(e.target.value)} />}</Field>
            <Field label="Name">{(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
            <Field label="Primary entitlement" help="e.g. vendor_ai.enabled">{(id) => <TextInput id={id} value={primaryEntitlement} onChange={(e) => setPrimaryEntitlement(e.target.value)} />}</Field>
            <Field label="Icon">{(id) => <TextInput id={id} value={icon} onChange={(e) => setIcon(e.target.value)} />}</Field>
          </div>
          {error && <p className="ods-error-text" role="alert">{error}</p>}
          <Button size="sm" onClick={create} disabled={!slug.trim() || !name.trim() || !primaryEntitlement.trim()}>Create application</Button>
        </CardBody>
      </Card>
    </>
  );
}

function EditApplication({ slug }: { slug: string }) {
  const detail = useAsync(async () => api.adminApplication(slug), [slug]);
  const reference = useAsync(async () => api.adminReference(), []);

  if (detail.loading || reference.loading) return <LoadingState label="Loading application…" />;
  if (detail.error) return <ErrorState description={detail.error.message} onRetry={detail.reload} />;
  if (!detail.data) return <ErrorState description="Application not found." />;

  return (
    <EditApplicationForm data={detail.data} reference={reference.data} onChanged={detail.reload} />
  );
}

function EditApplicationForm({ data, reference, onChanged }: { data: AdminApplicationDetail; reference: AdminReference | null; onChanged: () => void }) {
  const slug = data.application.slug;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>{data.application.icon} {data.application.name}</h1>
          <p><code>{slug}</code> · <Badge tone="neutral">{data.application.lifecycleStatus}</Badge></p>
        </div>
        <Link to="/studio/catalog-admin"><Button size="sm" variant="ghost">Back</Button></Link>
      </div>

      <DetailsSection data={data} reference={reference} onChanged={onChanged} />
      <LifecycleSection data={data} onChanged={onChanged} />
      <EntitlementsSection data={data} onChanged={onChanged} />
      <PricesSection data={data} reference={reference} onChanged={onChanged} />
      <TrialSection data={data} reference={reference} onChanged={onChanged} />
      <NavigationSection data={data} onChanged={onChanged} />
      <TrustSection data={data} onChanged={onChanged} />
      <ReleaseSection data={data} onChanged={onChanged} />
    </>
  );
}

function SaveFooter({ saver }: { saver: ReturnType<typeof useSaver> }) {
  return (
    <>
      {saver.notice && <p className="ods-help" role="status">{saver.notice}</p>}
      {saver.error && <p className="ods-error-text" role="alert">{saver.error}</p>}
    </>
  );
}

function DetailsSection({ data, reference, onChanged }: { data: AdminApplicationDetail; reference: AdminReference | null; onChanged: () => void }) {
  const a = data.application;
  const saver = useSaver();
  const [name, setName] = useState(a.name);
  const [tagline, setTagline] = useState(a.tagline);
  const [description, setDescription] = useState(a.description);
  const [icon, setIcon] = useState(a.icon);
  const [categoryId, setCategoryId] = useState(a.categoryId ?? "");
  const [planKey, setPlanKey] = useState(a.planKey ?? "");
  const [primaryEntitlement, setPrimaryEntitlement] = useState(a.primaryEntitlement);
  const [sortOrder, setSortOrder] = useState(String(a.sortOrder));

  return (
    <Card>
      <CardHeader title="Details" />
      <CardBody>
        <div className="ods-form-grid">
          <Field label="Name">{(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label="Icon">{(id) => <TextInput id={id} value={icon} onChange={(e) => setIcon(e.target.value)} />}</Field>
          <Field label="Tagline">{(id) => <TextInput id={id} value={tagline} onChange={(e) => setTagline(e.target.value)} />}</Field>
          <Field label="Primary entitlement">{(id) => <TextInput id={id} value={primaryEntitlement} onChange={(e) => setPrimaryEntitlement(e.target.value)} />}</Field>
          <Field label="Category">{(id) => (
            <Select id={id} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">None</option>
              {(reference?.categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          )}</Field>
          <Field label="Plan">{(id) => (
            <Select id={id} value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
              <option value="">None</option>
              {(reference?.plans ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}</Field>
          <Field label="Sort order">{(id) => <TextInput id={id} type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />}</Field>
        </div>
        <Field label="Description">{(id) => <TextArea id={id} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Details", () => api.updateAdminApplication(slug(data), {
          name, tagline, description, icon, categoryId: categoryId || null, planKey: planKey || null, primaryEntitlement, sortOrder: Number(sortOrder) || 0,
        }).then(onChanged))}>Save details</Button>
      </CardBody>
    </Card>
  );
}

function slug(data: AdminApplicationDetail): string {
  return data.application.slug;
}

function LifecycleSection({ data, onChanged }: { data: AdminApplicationDetail; onChanged: () => void }) {
  const saver = useSaver();
  const [status, setStatus] = useState<CatalogLifecycleStatus>(data.application.lifecycleStatus);
  const [maintenanceMessage, setMaintenanceMessage] = useState(data.application.maintenanceMessage ?? "");
  const [deprecationNotice, setDeprecationNotice] = useState(data.application.deprecationNotice ?? "");
  return (
    <Card>
      <CardHeader title="Lifecycle" />
      <CardBody>
        <div className="ods-form-grid">
          <Field label="Status" help={`Current: ${data.application.lifecycleStatus}`}>{(id) => (
            <Select id={id} value={status} onChange={(e) => setStatus(e.target.value as CatalogLifecycleStatus)}>
              {LIFECYCLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          )}</Field>
          <Field label="Maintenance message">{(id) => <TextInput id={id} value={maintenanceMessage} onChange={(e) => setMaintenanceMessage(e.target.value)} />}</Field>
          <Field label="Deprecation notice">{(id) => <TextInput id={id} value={deprecationNotice} onChange={(e) => setDeprecationNotice(e.target.value)} />}</Field>
        </div>
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Lifecycle", () => api.setAdminApplicationLifecycle(slug(data), {
          status, maintenanceMessage: maintenanceMessage || null, deprecationNotice: deprecationNotice || null,
        }).then(onChanged))}>Apply transition</Button>
      </CardBody>
    </Card>
  );
}

function EntitlementsSection({ data, onChanged }: { data: AdminApplicationDetail; onChanged: () => void }) {
  const saver = useSaver();
  const [rows, setRows] = useState(data.entitlements.map((e) => ({ ...e })));
  function update(i: number, patch: Partial<{ key: string; required: boolean; label: string }>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  return (
    <Card>
      <CardHeader title="Entitlement mappings" action={<Button size="sm" variant="ghost" onClick={() => setRows((p) => [...p, { key: "", required: true, label: "" }])}>Add</Button>} />
      <CardBody>
        {rows.length === 0 && <p className="ods-help">No entitlement mappings.</p>}
        {rows.map((r, i) => (
          <div className="ods-form-grid" key={i}>
            <Field label="Entitlement key">{(id) => <TextInput id={id} value={r.key} onChange={(e) => update(i, { key: e.target.value })} />}</Field>
            <Field label="Label">{(id) => <TextInput id={id} value={r.label} onChange={(e) => update(i, { label: e.target.value })} />}</Field>
            <Field label="Required">{(id) => (
              <Select id={id} value={r.required ? "yes" : "no"} onChange={(e) => update(i, { required: e.target.value === "yes" })}>
                <option value="yes">Required</option>
                <option value="no">Optional</option>
              </Select>
            )}</Field>
            <div className="ods-field" style={{ justifyContent: "flex-end" }}>
              <Button size="sm" variant="ghost" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}>Remove</Button>
            </div>
          </div>
        ))}
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Entitlements", () => api.setAdminApplicationEntitlements(slug(data), rows.filter((r) => r.key.trim())).then(onChanged))}>Save entitlements</Button>
      </CardBody>
    </Card>
  );
}

function PricesSection({ data, reference, onChanged }: { data: AdminApplicationDetail; reference: AdminReference | null; onChanged: () => void }) {
  const saver = useSaver();
  const [rows, setRows] = useState(data.prices.map((p) => ({ priceId: p.priceId, interval: p.interval })));
  const prices = reference?.prices ?? [];
  function update(i: number, patch: Partial<{ priceId: string; interval: string }>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  return (
    <Card>
      <CardHeader title="Pricing references" action={<Button size="sm" variant="ghost" onClick={() => setRows((p) => [...p, { priceId: "", interval: "month" }])}>Add</Button>} />
      <CardBody>
        <p className="ods-help">Pricing authority stays in the billing plans/prices. The catalog only references existing prices.</p>
        {rows.map((r, i) => (
          <div className="ods-form-grid" key={i}>
            <Field label="Price">{(id) => (
              <Select id={id} value={r.priceId} onChange={(e) => update(i, { priceId: e.target.value })}>
                <option value="">Select a price</option>
                {prices.map((p) => <option key={p.id} value={p.id}>{p.id} ({p.interval}, {(p.unitAmount / 100).toLocaleString(undefined, { style: "currency", currency: p.currency })})</option>)}
              </Select>
            )}</Field>
            <Field label="Interval">{(id) => (
              <Select id={id} value={r.interval} onChange={(e) => update(i, { interval: e.target.value })}>
                <option value="month">month</option>
                <option value="year">year</option>
              </Select>
            )}</Field>
            <div className="ods-field" style={{ justifyContent: "flex-end" }}>
              <Button size="sm" variant="ghost" onClick={() => setRows((p) => p.filter((_, idx) => idx !== i))}>Remove</Button>
            </div>
          </div>
        ))}
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Prices", () => api.setAdminApplicationPrices(slug(data), rows.filter((r) => r.priceId)).then(onChanged))}>Save prices</Button>
      </CardBody>
    </Card>
  );
}

function TrialSection({ data, reference, onChanged }: { data: AdminApplicationDetail; reference: AdminReference | null; onChanged: () => void }) {
  const saver = useSaver();
  const tp = data.trialPolicy;
  const [enabled, setEnabled] = useState(tp?.enabled ?? false);
  const [trialDays, setTrialDays] = useState(String(tp?.trialDays ?? 14));
  const [planKey, setPlanKey] = useState(tp?.planKey ?? "");
  return (
    <Card>
      <CardHeader title="Trial policy" />
      <CardBody>
        <div className="ods-form-grid">
          <Field label="Trials enabled">{(id) => (
            <Select id={id} value={enabled ? "yes" : "no"} onChange={(e) => setEnabled(e.target.value === "yes")}>
              <option value="yes">Enabled</option>
              <option value="no">Disabled</option>
            </Select>
          )}</Field>
          <Field label="Trial days">{(id) => <TextInput id={id} type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />}</Field>
          <Field label="Trial plan">{(id) => (
            <Select id={id} value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
              <option value="">None</option>
              {(reference?.plans ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          )}</Field>
        </div>
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Trial policy", () => api.setAdminApplicationTrialPolicy(slug(data), { enabled, trialDays: Number(trialDays) || 14, planKey: planKey || null }).then(onChanged))}>Save trial policy</Button>
      </CardBody>
    </Card>
  );
}

function NavigationSection({ data, onChanged }: { data: AdminApplicationDetail; onChanged: () => void }) {
  const saver = useSaver();
  const nav = data.navigation;
  const [route, setRoute] = useState(nav?.route ?? "");
  const [navLabel, setNavLabel] = useState(nav?.navLabel ?? "");
  const [navSection, setNavSection] = useState(nav?.navSection ?? "apps");
  const [sortOrder, setSortOrder] = useState(String(nav?.sortOrder ?? 0));
  return (
    <Card>
      <CardHeader title="Navigation" />
      <CardBody>
        <div className="ods-form-grid">
          <Field label="Route">{(id) => <TextInput id={id} value={route} onChange={(e) => setRoute(e.target.value)} placeholder="/studio/app-slug" />}</Field>
          <Field label="Nav label">{(id) => <TextInput id={id} value={navLabel} onChange={(e) => setNavLabel(e.target.value)} />}</Field>
          <Field label="Nav section">{(id) => <TextInput id={id} value={navSection} onChange={(e) => setNavSection(e.target.value)} />}</Field>
          <Field label="Sort order">{(id) => <TextInput id={id} type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />}</Field>
        </div>
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Navigation", () => api.setAdminApplicationNavigation(slug(data), { route: route || null, navLabel, navSection, sortOrder: Number(sortOrder) || 0 }).then(onChanged))}>Save navigation</Button>
      </CardBody>
    </Card>
  );
}

function TrustSection({ data, onChanged }: { data: AdminApplicationDetail; onChanged: () => void }) {
  const saver = useSaver();
  const t = data.trust;
  const [trustCenterUrl, setTrustCenterUrl] = useState(t?.trustCenterUrl ?? "");
  const [dataHandling, setDataHandling] = useState(t?.dataHandling ?? "");
  const [certifications, setCertifications] = useState((t?.certifications ?? []).join(", "));
  const [securityNotes, setSecurityNotes] = useState(t?.securityNotes ?? "");
  return (
    <Card>
      <CardHeader title="Trust Center metadata" />
      <CardBody>
        <div className="ods-form-grid">
          <Field label="Trust Center URL">{(id) => <TextInput id={id} value={trustCenterUrl} onChange={(e) => setTrustCenterUrl(e.target.value)} />}</Field>
          <Field label="Certifications" help="Separate with commas, e.g. SOC 2, ISO 27001">{(id) => <TextInput id={id} value={certifications} onChange={(e) => setCertifications(e.target.value)} />}</Field>
        </div>
        <Field label="Data handling">{(id) => <TextArea id={id} rows={2} value={dataHandling} onChange={(e) => setDataHandling(e.target.value)} />}</Field>
        <Field label="Security notes">{(id) => <TextArea id={id} rows={2} value={securityNotes} onChange={(e) => setSecurityNotes(e.target.value)} />}</Field>
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Trust metadata", () => api.setAdminApplicationTrust(slug(data), {
          trustCenterUrl: trustCenterUrl || null, dataHandling, certifications: certifications.split(",").map((s) => s.trim()).filter(Boolean), securityNotes,
        }).then(onChanged))}>Save trust metadata</Button>
      </CardBody>
    </Card>
  );
}

function ReleaseSection({ data, onChanged }: { data: AdminApplicationDetail; onChanged: () => void }) {
  const saver = useSaver();
  const versionSaver = useSaver();
  const r = data.release;
  const [currentVersion, setCurrentVersion] = useState(r?.currentVersion ?? "");
  const [releaseNotesUrl, setReleaseNotesUrl] = useState(r?.releaseNotesUrl ?? "");
  const [releasedAt, setReleasedAt] = useState(r?.releasedAt ?? "");
  const [knownLimitations, setKnownLimitations] = useState(r?.knownLimitations ?? "");
  const [newVersion, setNewVersion] = useState("");
  const [newVersionNotes, setNewVersionNotes] = useState("");
  return (
    <Card>
      <CardHeader title="Release metadata" />
      <CardBody>
        <div className="ods-form-grid">
          <Field label="Current version">{(id) => <TextInput id={id} value={currentVersion} onChange={(e) => setCurrentVersion(e.target.value)} />}</Field>
          <Field label="Release notes URL">{(id) => <TextInput id={id} value={releaseNotesUrl} onChange={(e) => setReleaseNotesUrl(e.target.value)} />}</Field>
          <Field label="Released at">{(id) => <TextInput id={id} value={releasedAt} onChange={(e) => setReleasedAt(e.target.value)} placeholder="2026-01-01" />}</Field>
        </div>
        <Field label="Known limitations">{(id) => <TextArea id={id} rows={2} value={knownLimitations} onChange={(e) => setKnownLimitations(e.target.value)} />}</Field>
        <SaveFooter saver={saver} />
        <Button size="sm" disabled={saver.busy} onClick={() => saver.run("Release metadata", () => api.setAdminApplicationRelease(slug(data), {
          currentVersion, releaseNotesUrl: releaseNotesUrl || null, releasedAt: releasedAt || null, knownLimitations,
        }).then(onChanged))}>Save release metadata</Button>

        <hr className="ods-divider" />
        <p className="ods-help">Add a changelog version entry ({data.versions.length} existing).</p>
        <div className="ods-form-grid">
          <Field label="Version">{(id) => <TextInput id={id} value={newVersion} onChange={(e) => setNewVersion(e.target.value)} placeholder="1.1.0" />}</Field>
          <Field label="Notes">{(id) => <TextInput id={id} value={newVersionNotes} onChange={(e) => setNewVersionNotes(e.target.value)} />}</Field>
        </div>
        <SaveFooter saver={versionSaver} />
        <Button size="sm" variant="secondary" disabled={versionSaver.busy || !newVersion.trim()} onClick={() => versionSaver.run("Version", () => api.addAdminApplicationVersion(slug(data), { version: newVersion.trim(), notes: newVersionNotes, isCurrent: true }).then(() => { setNewVersion(""); setNewVersionNotes(""); onChanged(); }))}>Add version</Button>
      </CardBody>
    </Card>
  );
}
